import React from 'react';
import { useInView } from 'react-intersection-observer';
import { TrendingUp, Users, DollarSign, Clock, ArrowRight } from 'lucide-react';

const CaseStudies = () => {
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { ref: studiesRef, inView: studiesInView } = useInView({ threshold: 0.1, triggerOnce: true });

  const caseStudies = [
    {
      company: 'TechFlow Solutions',
      industry: 'SaaS Technology',
      challenge: 'Manual processes consuming 30+ hours weekly, low conversion rates, and inefficient customer support',
      solution: 'Implemented AI automation suite with custom CRM integration, automated lead scoring, and 24/7 AI support chatbot',
      results: [
        { metric: '200%', label: 'Conversion Rate Increase' },
        { metric: '30hrs', label: 'Weekly Time Saved' },
        { metric: '85%', label: 'Support Automation' },
        { metric: '3 months', label: 'ROI Timeline' }
      ],
      testimonial: 'American Prime transformed our digital presence completely. The AI automation saved us 30+ hours per week, and our conversion rate doubled within 3 months.',
      author: 'Sarah Chen',
      role: 'CEO, TechFlow Solutions',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      company: 'GrowthLab',
      industry: 'Digital Marketing',
      challenge: 'Struggling with lead quality, manual CRM data entry, and scattered sales processes across multiple tools',
      solution: 'Deployed integrated automation platform with AI-powered lead generation, CRM workflow automation, and unified sales dashboard',
      results: [
        { metric: '250%', label: 'Lead Generation Increase' },
        { metric: '95%', label: 'Process Automation' },
        { metric: '$500K', label: 'Additional Revenue' },
        { metric: '60%', label: 'Cost Reduction' }
      ],
      testimonial: 'The team delivered beyond expectations. Our lead generation increased by 250%, and the CRM integration streamlined our entire sales process.',
      author: 'Marcus Rodriguez',
      role: 'Marketing Director, GrowthLab',
      image: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      company: 'Stellar Brands',
      industry: 'E-commerce',
      challenge: 'High customer acquisition costs, low retention rates, and manual inventory management',
      solution: 'Built comprehensive AI solution with automated marketing campaigns, predictive inventory system, and personalized customer engagement',
      results: [
        { metric: '5x', label: 'Return on Investment' },
        { metric: '40%', label: 'Customer Retention Boost' },
        { metric: '70%', label: 'Inventory Efficiency' },
        { metric: '4 months', label: 'Payback Period' }
      ],
      testimonial: 'Working with American Prime was a game-changer. Their strategic approach and attention to detail resulted in a 5x ROI in just 4 months.',
      author: 'Emily Watson',
      role: 'Founder, Stellar Brands',
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400'
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', paddingTop: '80px' }}>
      {/* Hero */}
      <section
        ref={heroRef}
        className="py-24"
        style={{ padding: '120px 7.6923% 80px', background: 'var(--bg-primary)' }}
      >
        <div className={`max-w-5xl mx-auto text-center ${heroInView ? 'fade-in' : 'opacity-0'}`}>
          <div className="body-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
            Case Studies
          </div>
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Real Results,
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Real Impact
            </span>
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            See how we've helped businesses transform their operations, scale efficiently, and achieve measurable growth through AI-powered solutions.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section
        ref={studiesRef}
        className="py-12"
        style={{ padding: '60px 7.6923% 100px', background: 'var(--bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto space-y-24">
          {caseStudies.map((study, index) => (
            <div
              key={index}
              className={`${studiesInView ? 'fade-in' : 'opacity-0'}`}
              style={{
                animationDelay: `${index * 0.2}s`
              }}
            >
              {/* Company Header */}
              <div className="mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div
                    className="px-4 py-1 rounded-full"
                    style={{
                      background: 'rgba(0, 255, 209, 0.1)',
                      border: '1px solid rgba(0, 255, 209, 0.3)'
                    }}
                  >
                    <span className="body-small" style={{ color: 'var(--brand-primary)' }}>
                      {study.industry}
                    </span>
                  </div>
                </div>
                <h2 className="display-medium" style={{ color: 'var(--text-primary)' }}>
                  {study.company}
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Left: Challenge & Solution */}
                <div className="space-y-8">
                  <div>
                    <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
                      The Challenge
                    </h3>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {study.challenge}
                    </p>
                  </div>

                  <div>
                    <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
                      Our Solution
                    </h3>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {study.solution}
                    </p>
                  </div>

                  {/* Testimonial */}
                  <div
                    className="p-6 rounded-lg"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid var(--border-subtle)'
                    }}
                  >
                    <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      "{study.testimonial}"
                    </p>
                    <div className="flex items-center space-x-4">
                      <img
                        src={study.image}
                        alt={study.author}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="heading-3" style={{ color: 'var(--text-primary)' }}>
                          {study.author}
                        </div>
                        <div className="body-small" style={{ color: 'var(--text-muted)' }}>
                          {study.role}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Results */}
                <div>
                  <h3 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
                    The Results
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    {study.results.map((result, idx) => (
                      <div
                        key={idx}
                        className="p-6 rounded-lg dark-hover"
                        style={{
                          background: 'rgba(0, 255, 209, 0.05)',
                          border: '1px solid rgba(0, 255, 209, 0.2)'
                        }}
                      >
                        <div className="display-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
                          {result.metric}
                        </div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {result.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {index < caseStudies.length - 1 && (
                <div
                  className="mt-16"
                  style={{
                    height: '1px',
                    background: 'var(--border-subtle)'
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24"
        style={{
          padding: '100px 7.6923%',
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 255, 209, 0.02) 100%)',
          borderTop: '1px solid rgba(0, 255, 209, 0.1)',
          borderBottom: '1px solid rgba(0, 255, 209, 0.1)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            Ready to Write
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Your Success Story?
            </span>
          </h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Let's discuss how we can help you achieve similar results
          </p>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="button-text dark-button-animate inline-flex items-center space-x-2"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '14px 32px',
              borderRadius: '0px',
              textDecoration: 'none'
            }}
          >
            <span>Book Your Free Consultation</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;